import React, { useEffect, useMemo, useState } from 'react';
import { sendQuoteEmail } from '../services/emailService';
import { resolveMediaUrl } from '../services/productsApi';
import { downloadQuotePdf } from '../utils/buildQuotePdf';

const QUOTE_SEND_COOLDOWN_MS = 60 * 60 * 1000;
const QUOTE_SEND_STORAGE_KEY = 'verissa-quote-sent-at';

const readCooldownRemaining = () => {
  try {
    const sentAt = Number(window.localStorage.getItem(QUOTE_SEND_STORAGE_KEY));
    if (!sentAt) return 0;
    return Math.max(0, sentAt + QUOTE_SEND_COOLDOWN_MS - Date.now());
  } catch {
    return 0;
  }
};

const formatCooldown = (remainingMs) => {
  const minutes = Math.max(1, Math.ceil(remainingMs / 60000));
  if (minutes >= 60) return '1 hora';
  return minutes === 1 ? '1 minuto' : `${minutes} minutos`;
};

const QuoteCart = ({ items, onRemoveItem, onClearItems }) => {
  const hasItems = items && items.length > 0;
  const [formData, setFormData] = useState({
    nombre: '',
    correo: '',
    telefono: '',
    comentario: '',
  });
  const [isSending, setIsSending] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [statusType, setStatusType] = useState('');
  const [cooldownRemaining, setCooldownRemaining] = useState(readCooldownRemaining);

  useEffect(() => {
    if (cooldownRemaining <= 0) return undefined;
    const timer = window.setInterval(() => {
      setCooldownRemaining(readCooldownRemaining());
    }, 15000);
    return () => window.clearInterval(timer);
  }, [cooldownRemaining]);

  const quoteDetails = useMemo(
    () =>
      (items || [])
        .map(
          (item, index) => {
            const attrs = (item.selectedAttributes || [])
              .map((attr) => `${attr.attributeLabel}: ${attr.optionLabel}`)
              .join(', ');
            const attrsLine = attrs ? ` | ${attrs}` : '';
            return `${index + 1}. ${item.name} | ${item.category}${attrsLine} | Cantidad: ${item.quantity}`;
          }
        )
        .join('\n'),
    [items]
  );

  const handleDownloadPdf = async () => {
    if (!hasItems || isDownloadingPdf) return;

    setIsDownloadingPdf(true);
    setStatusMessage('');
    setStatusType('');

    try {
      await downloadQuotePdf({
        items,
        client: formData,
      });
    } catch (error) {
      setStatusType('error');
      setStatusMessage(
        'No se pudo generar el PDF en este momento. Intenta nuevamente.'
      );
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSendQuote = async (e) => {
    e.preventDefault();
    if (!hasItems) return;

    if (readCooldownRemaining() > 0) {
      setCooldownRemaining(readCooldownRemaining());
      return;
    }

    const nombre = formData.nombre.trim();
    const correo = formData.correo.trim();
    if (!nombre || !correo) {
      setStatusType('error');
      setStatusMessage(
        'Por favor llena los campos obligatorios: nombre y correo electrónico.'
      );
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(correo)) {
      setStatusType('error');
      setStatusMessage('Por favor ingresa un correo electrónico válido.');
      return;
    }

    setIsSending(true);
    setStatusMessage('');
    setStatusType('');

    try {
      await sendQuoteEmail({
        from_name: nombre,
        from_email: correo,
        phone: formData.telefono || 'No proporcionado',
        message: formData.comentario || 'Sin comentarios adicionales.',
        quote_items: quoteDetails,
      });

      const sentAt = Date.now();
      try {
        window.localStorage.setItem(QUOTE_SEND_STORAGE_KEY, String(sentAt));
      } catch {
        // Si el navegador bloquea el almacenamiento, el envío igual ya ocurrió.
      }
      setCooldownRemaining(QUOTE_SEND_COOLDOWN_MS);
      setStatusType('success');
      setStatusMessage(
        'La solicitud se envió correctamente. La próxima cotización se puede realizar dentro de 1 hora.'
      );
      setFormData({ nombre: '', correo: '', telefono: '', comentario: '' });
      if (onClearItems) onClearItems();
      if (typeof window.gtag_report_conversion === 'function') {
        window.gtag_report_conversion();
      }
    } catch (error) {
      setStatusType('error');
      setStatusMessage(
        'No se pudo enviar la solicitud en este momento. Intenta nuevamente.'
      );
    } finally {
      setIsSending(false);
    }
  };

  return (
    <section className="quote-cart-page">
      <div className="quote-cart-header">
        <h1 className="section-title">Carrito de Cotización</h1>
        <p className="quote-cart-intro">
          Aquí armas una lista de los productos cuyo precio quieres conocer o
          sobre los que necesitas más información. Agrega los que te interesen
          y envía la solicitud con tu nombre y correo. No es una compra. El
          equipo de VERISSA confirma precios y disponibilidad.
        </p>
      </div>

      {statusMessage && (
        <p className={`quote-cart-status quote-cart-status-banner ${statusType}`}>
          {statusMessage}
        </p>
      )}

      {!hasItems && (
        <div className="quote-cart-empty">
          <p>No has agregado productos al carrito de cotización.</p>
          <p>Ve a la sección de productos y selecciona los que te interesan.</p>
        </div>
      )}

      {hasItems && (
        <>
          <div className="quote-cart-actions">
            <button
              type="button"
              className="quote-cart-download-btn"
              onClick={handleDownloadPdf}
              disabled={isDownloadingPdf}
            >
              {isDownloadingPdf
                ? 'Generando PDF...'
                : 'Descargar PDF de cotización'}
            </button>
          </div>
          <div className="quote-cart-mail-wrapper">
            <h3>Enviar carrito por correo</h3>
            <form
              className="quote-cart-mail-form"
              onSubmit={handleSendQuote}
              noValidate
            >
              <div className="quote-cart-mail-grid">
                <div className="form-row">
                  <label htmlFor="quote-nombre">Nombre *</label>
                  <input
                    id="quote-nombre"
                    name="nombre"
                    type="text"
                    value={formData.nombre}
                    onChange={handleInputChange}
                    placeholder="Tu nombre"
                  />
                </div>
                <div className="form-row">
                  <label htmlFor="quote-correo">Correo electrónico *</label>
                  <input
                    id="quote-correo"
                    name="correo"
                    type="email"
                    value={formData.correo}
                    onChange={handleInputChange}
                    placeholder="correo@ejemplo.com"
                  />
                </div>
                <div className="form-row">
                  <label htmlFor="quote-telefono">Telefono</label>
                  <input
                    id="quote-telefono"
                    name="telefono"
                    type="tel"
                    value={formData.telefono}
                    onChange={handleInputChange}
                    placeholder="+52"
                  />
                </div>
              </div>
              <div className="form-row">
                <label htmlFor="quote-comentario">Comentario adicional</label>
                <textarea
                  id="quote-comentario"
                  name="comentario"
                  rows={3}
                  value={formData.comentario}
                  onChange={handleInputChange}
                  placeholder="Escribe una nota para tu solicitud de cotizacion..."
                />
              </div>
              <button
                type="submit"
                className="quote-cart-send-btn"
                disabled={isSending || cooldownRemaining > 0}
              >
                {isSending
                  ? 'Enviando solicitud...'
                  : 'Enviar solicitud de cotizacion'}
              </button>
              {cooldownRemaining > 0 && (
                <p className="quote-cart-cooldown">
                  Podrás enviar otra solicitud en {formatCooldown(cooldownRemaining)}.
                </p>
              )}
            </form>
          </div>
          <div className="quote-cart-list">
            {items.map((item) => (
              <article key={item.id} className="quote-cart-item">
                <div className="quote-cart-item-main">
                  <div className="quote-cart-item-image">
                    {item.images?.[0] && (
                      <img
                        src={resolveMediaUrl(item.images[0])}
                        alt={item.name}
                      />
                    )}
                  </div>
                  <div className="quote-cart-item-info">
                    <span className="product-category">{item.category}</span>
                    <h2 className="quote-cart-item-title">{item.name}</h2>
                    {item.selectedAttributes?.length > 0 && (
                      <ul className="quote-cart-item-attributes">
                        {item.selectedAttributes.map((attr) => (
                          <li key={`${attr.attributeLabel}-${attr.optionLabel}`}>
                            <strong>{attr.attributeLabel}:</strong>{' '}
                            {attr.optionLabel}
                          </li>
                        ))}
                      </ul>
                    )}
                    <p className="quote-cart-item-description">
                      {item.description}
                    </p>
                  </div>
                </div>
                <div className="quote-cart-item-meta">
                  <span className="quote-cart-qty-label">
                    Cantidad solicitada
                  </span>
                  <span className="quote-cart-qty-value">{item.quantity}</span>
                  {onRemoveItem && (
                    <button
                      type="button"
                      className="quote-cart-remove-btn"
                      onClick={() => onRemoveItem(item.id)}
                      aria-label="Eliminar producto del carrito de cotización"
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                        <path d="M10 11v6" />
                        <path d="M14 11v6" />
                        <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
                      </svg>
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
        </>
      )}
    </section>
  );
};

export default QuoteCart;

